import { ENDPOINTS } from "../utils/appConfig";
import { fetchWithAuthRetry } from "../utils/helpers";
import { getItem, setItem } from "../utils/authStorage";

export async function loginUser(username, password) {
  try {
    const response = await fetch(ENDPOINTS.LOGIN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    if (!response.ok) {
      let errorMessage = 'Login failed';
      if (isJson) {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.error || errorMessage;
      } else {
        errorMessage = `Server Error (${response.status}): ${response.statusText || 'Backend server is unreachable'}`;
      }
      throw new Error(errorMessage);
    }

    if (isJson) {
      return await response.json();
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
}

export async function refreshAccessToken() {
  try {
    const refreshToken = getItem('refreshToken');
    const response = await fetch(ENDPOINTS.REFRESH_TOKEN, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    }
    if (!response.ok) {
      console.error("Refresh failed:", data);
      throw new Error(data?.detail || 'Failed to refresh token');
    }

    if (data.access) setItem('accessToken', data.access);
    if (data.refresh) setItem('refreshToken', data.refresh);

    // The asset cookie expires with the old access token; reissue it or images/tiles start returning 401.
    if (data.access) {
      await establishAssetSession().catch((err) => console.warn('Asset session refresh failed:', err));
    }

    return data.access;
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
}

/**
 * (Re)issue the HttpOnly asset cookie used by nginx to authorize
 * /site-visuals/ and /media/ (img, tiles, HLS, GLB cannot send Bearer headers).
 */
export async function establishAssetSession() {
  const response = await fetch(ENDPOINTS.ASSET_SESSION, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getItem('accessToken')}` },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Asset session failed (${response.status})`);
}

export async function fetchAuthList() {
  try {
    const response = await fetchWithAuthRetry(ENDPOINTS.AUTH_LIST, {
      method: 'GET',
      headers: { Authorization: `Bearer ${getItem('accessToken')}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch auth profile:', error);
    throw error;
  }
}

export async function fetchRoles() {
  try {
    const response = await fetchWithAuthRetry(ENDPOINTS.ROLE_LIST, {
      method: 'GET',
      headers: { Authorization: `Bearer ${getItem('accessToken')}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch roles:', error);
    throw error;
  }
}

export async function registerUser(payload) {
  try {
    let response = await fetch(ENDPOINTS.AUTH_LIST, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getItem('accessToken')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.status === 401 || response.status === 403) {
      console.warn('Access token expired. Attempting to refresh...');
      const newAccessToken = await refreshAccessToken();
      response = await fetch(ENDPOINTS.AUTH_LIST, {
        method: 'POST',
        headers: { Authorization: `Bearer ${newAccessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }
    return response;
  } catch (error) {
    throw error;
  }
}

export async function updateUser(payload) {
  try {
    let response = await fetch(ENDPOINTS.AUTH_LIST_UPDATE(payload.id), {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${getItem('accessToken')}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (response.status === 401 || response.status === 403) {
      console.warn('Access token expired. Attempting to refresh...');
      const newAccessToken = await refreshAccessToken();
      response = await fetch(ENDPOINTS.AUTH_LIST_UPDATE(payload.id), {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${newAccessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    }
    return response;
  } catch (error) {
    throw error;
  }
}

export async function fetchTenants() {
  try {
    const response = await fetchWithAuthRetry(ENDPOINTS.TENANTS, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getItem('accessToken')}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
    return await response.json();
  } catch (error) {
    console.error('Failed to fetch tenants:', error);
    throw error;
  }
}

export async function createTenant(payload) {
  try {
    let response = await fetch(ENDPOINTS.TENANTS, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getItem('accessToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.status === 401 || response.status === 403) {
      const newAccessToken = await refreshAccessToken();
      response = await fetch(ENDPOINTS.TENANTS, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${newAccessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
    }
    return response;
  } catch (error) {
    console.error('Failed to create tenant:', error);
    throw error;
  }
}

